import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { colors, styles } from '../styles/styles';
import { 
    addThoughtsToCollectionBatch, 
    removeThoughtsFromCollectionBatch 
} from '../services/collectionService';
import SortDrawer from '../components/SortDrawer';
import FilterDrawer from '../components/FilterDrawer';
import PageHeader from '../components/PageHeader';
import SelectedCounter from '../components/common/SelectedCounter';
import StaggeredAnimation from '../components/common/StaggeredAnimation';
import SearchInput from '../components/common/SearchInput';
import { useTagFilters } from '../utils/tagUtils';
import { getPopularTags } from '../utils/tagUtils';
import useSortState from '../hooks/useSortState';
import useCollectionThoughts from '../hooks/useCollectionThoughts';
import { filterThoughtsByTags, filterThoughtsBySearchTerm } from '../utils/filterUtils';
import useSelectionState from '../hooks/useSelectionState';
import { handleRelationshipUpdate } from '../utils/collectionUtils';
import { sortThoughts } from '../utils/sortUtils';
import TagFilters from '../components/TagFilters';
import ToolsBar from '../components/common/ToolsBar';
import Identicon from '../components/common/Identicon';
import { formatTagName } from '../utils/formatters';

/**
 * Page for adding thoughts to a collection
 */
const AddToCollectionPage = ({ thoughts, navigateWithFade }) => {
    const { collectionId } = useParams();
    const location = useLocation();
    const collectionName = location.state?.collectionName || "Collection";
    const isDefault = location.state?.isDefault || false;
    const isTagCollection = location.state?.isTagCollection || false;
    
    // Format collection name if it's a tag collection
    const displayName = isTagCollection ? formatTagName(collectionName) : collectionName;
    const [existingThoughtIds, setExistingThoughtIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    
    // Search, sort and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, handleSortChange] = useSortState('recent');
    const [showSortDrawer, setShowSortDrawer] = useState(false);
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);
    const { activeFilters, setActiveFilters, toggleTag, hasActiveFilters } = useTagFilters({ tags: [] });
    const [popularTags, setPopularTags] = useState([]);
    
    // Use selection state hook for thought selection
    const { 
        selectedItems: selectedThoughtIds, 
        setSelectedItems: setSelectedThoughtIds,
        toggleItem: toggleThoughtSelection,
        isSelected: isThoughtSelected,
        count: selectedCount
    } = useSelectionState([]);
    
    // Use the hook to fetch collection thoughts
    const { collectionThoughts } = useCollectionThoughts(collectionId, false);
    
    // Initialize selected thoughts on first non-empty load
    useEffect(() => {
        // only initialize on the very first non‑empty load
        if (
            existingThoughtIds.length === 0 &&
            collectionThoughts.length > 0
        ) {
            setExistingThoughtIds(collectionThoughts.map(t => t.id));
            setSelectedThoughtIds(collectionThoughts.map(t => t.id));
        }
    }, [existingThoughtIds, collectionThoughts, setSelectedThoughtIds]);
    
    // All thoughts are available - we'll show checkmarks for ones already in collection
    const availableThoughts = thoughts;
    
    // Apply filters using utility functions
    const tagFilteredThoughts = filterThoughtsByTags(availableThoughts, activeFilters.tags);
    const filteredThoughts = filterThoughtsBySearchTerm(tagFilteredThoughts, searchTerm);
    
    // Handle navigating back
    const handleBack = () => {
        navigateWithFade(`/collection/${collectionId}`, { 
            collectionName,
            isDefault,
            isTagCollection
        });
    };
    
    // Handle adding selected thoughts to the collection
    const handleAddThoughts = async () => {
        if (isSubmitting) return;
        
        await handleRelationshipUpdate({
            selectedIds: selectedThoughtIds,
            existingIds: existingThoughtIds,
            addItemsFunction: addThoughtsToCollectionBatch,
            removeItemsFunction: removeThoughtsFromCollectionBatch,
            targetId: collectionId,
            setIsSubmitting,
            setSuccessMessage,
            navigateWithFade,
            navigateToPath: `/collection/${collectionId}`,
            navigationParams: { collectionName, isDefault, isTagCollection },
            itemType: 'thought'
        });
    };
    
    // Calculate popular tags on component mount or when thoughts change
    useEffect(() => {
        setPopularTags(getPopularTags(thoughts));
    }, [thoughts]);

    // Handle filter change
    const handleFilterChange = (newFilters) => {
        setActiveFilters(newFilters);
    };
    
    // Sort thoughts using the utility function
    const sortedThoughts = sortThoughts(filteredThoughts, sortBy);
    
    return (
        <div style={{ width: '100%' }} className="smooth-scroll">
            {/* Remove fade animation styles since AppContent handles transitions */}
            <StaggeredAnimation containerClass="thoughts-feed" />
            
            {/* Header with back button */}
            <PageHeader 
                onBack={handleBack}
                title={{ text: displayName, large: false }}
                rightContent={
                    <SelectedCounter count={selectedCount} />
                }
            />
            
            {/* Search and tools row - DESKTOP */}
            <div className="desktop-layout" style={styles.pageHeaderContainer}>
                <div style={styles.desktopUtilityBarContainer}>
                    {/* Search bar */}
                    <SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search thoughts..."
                        type="desktop"
                    />
                    
                    <ToolsBar
                        onSortClick={() => setShowSortDrawer(true)}
                        onFilterClick={() => setShowFilterDrawer(true)}
                        hasActiveFilters={hasActiveFilters}
                    />
                </div>
            </div>
            
            {/* Horizontal scrollable tag filters */}
            <TagFilters 
                popularTags={popularTags}
                activeFilters={activeFilters.tags}
                toggleTag={toggleTag}
                clearFilters={() => setActiveFilters({ tags: [] })}
                reserveSpaceWhenEmpty={true}
            />
            
            {/* Content */}
            <div style={{ width: '100%' }}>
                {successMessage ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '20px', 
                        color: successMessage.includes('Failed') ? colors.error : colors.accent.green,
                        fontWeight: '500'
                    }}>
                        {successMessage}
                    </div>
                ) : (searchTerm || hasActiveFilters) && sortedThoughts.length === 0 ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 0', 
                        color: colors.textSecondary,
                        marginTop: '16px'
                    }}>
                        <p>No thoughts match your search or filters.</p>
                    </div>
                ) : (
                    <div className="thoughts-feed" style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        marginBottom: '80px' // Space for the footer
                    }}>
                        {sortedThoughts.map((thought, index) => (
                            <div 
                                key={thought.id}
                                onClick={() => toggleThoughtSelection(thought.id)}
                                className="list-item"
                                style={{ 
                                    padding: '16px',
                                    cursor: 'pointer',
                                    width: '100%',
                                    backgroundColor: colors.background,
                                    borderBottom: index === sortedThoughts.length - 1 ? 'none' : `1px solid ${colors.divider}`,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                }}
                            >
                                {/* Checkmark indicator */}
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    marginRight: '12px',
                                    marginTop: '2px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexShrink: 0
                                }}>
                                    {isThoughtSelected(thought.id) && (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20 6L9 17L4 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </div>
                                
                                {/* Use Identicon directly with its built-in wrapper */}
                                <Identicon seed={thought.id} size={36} />
                                
                                {/* Thought content */}
                                <div style={{
                                    flex: 1,
                                    fontSize: '15px',
                                    lineHeight: '1.5',
                                    color: colors.textPrimary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                }}>
                                    {thought.thought || ''}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Footer with action buttons */}
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '16px',
                    backgroundColor: colors.surfaceElevated,
                    borderTop: `1px solid ${colors.divider}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    zIndex: 10
                }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={handleBack}
                            style={{
                                backgroundColor: 'transparent',
                                color: colors.accent.white,
                                border: 'none',
                                borderRadius: '20px',
                                padding: '8px 16px',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>
                        
                        <button
                            onClick={handleAddThoughts}
                            disabled={selectedThoughtIds.length === 0 || isSubmitting}
                            style={{
                                backgroundColor: colors.accent.white,
                                color: 'black',
                                border: 'none',
                                borderRadius: '20px',
                                padding: '8px 16px',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                cursor: (selectedThoughtIds.length > 0 && !isSubmitting) ? 'pointer' : 'not-allowed',
                                opacity: (selectedThoughtIds.length > 0 && !isSubmitting) ? 1 : 0.5
                            }}
                        >
                            {isSubmitting ? 'Updating...' : 'Save'}
                        </button>
                    </div>
                </div>
                
                {/* Sort drawer component */}
                <SortDrawer
                    isVisible={showSortDrawer}
                    onClose={() => setShowSortDrawer(false)}
                    onSortChange={handleSortChange}
                    currentSort={sortBy}
                />
                
                {/* Filter drawer component */}
                <FilterDrawer
                    isVisible={showFilterDrawer}
                    onClose={() => setShowFilterDrawer(false)}
                    onFilterChange={handleFilterChange}
                    currentFilters={activeFilters}
                    thoughts={availableThoughts}
                />
            </div>
        </div>
    );
};

export default AddToCollectionPage; 